import React, { FC, useEffect, useMemo, useState } from 'react';

import { ReactComponent as EditIcon } from 'assets/images/icons/edit.svg';
import Button from 'components/common/ui-kit/Button';
import ReactSelect from 'components/common/ui-kit/Select/view';
import ModeratorDataList from 'components/moderator/ModeratorDataList';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import terms from 'i18n';
import { ApplicationEducationLevel } from 'models/applications/enum';
import { SelectOption } from 'models/common';
import { EducationalDocumentType } from 'models/staff/upload-queue';
import { UserRole } from 'models/user';
import { createToast, showModal } from 'redux/actions';
import { apiSlice } from 'redux/api/api-slice';
import { staffApplicationsApi } from 'redux/api/staff/staff-applications-api';
import { selectUserRole } from 'redux/selectors';
import { formatDateYYYYMMDD } from 'utils/date';

import Loader from '../../../components/common/ui-kit/Loader';
import CheckListEntryValueSelectWithRoleChecker from '../../../components/moderator/CheckListEntryValueSelectWithRoleChecker';
import termsRu from '../../../i18n/terms.ru';
import {
  EducationInformationDraft,
  checkStatusesToStrings,
} from '../../../models/questionnaire/questionnaire';
import { applicationsInfoApi } from '../../../redux/api/applications/applications-info-api';
import {
  selectCurrentApplication,
  selectCurrentQuestionnaire,
} from '../../../redux/selectors/staff/applications';
import { selectCurrentSupportQuestionnaire } from '../../../redux/selectors/staff/questionnaires';

const EducationInfoSelect: FC<{
  userId: number;
  applicationId: number;
  educationProfileId?: number;
  educationalLevel: ApplicationEducationLevel;
}> = ({ userId, applicationId, educationalLevel, educationProfileId }) => {
  const dispatch = useAppDispatch();
  const role = useAppSelector(selectUserRole);
  const { data, isLoading } =
    applicationsInfoApi.useGetUserEducationalInformationQuery({
      userId,
      educationalLevel,
    });

  const [changeEducationalInformation] =
    staffApplicationsApi.useChangeEducationalInformationMutation();

  const options = useMemo(
    () =>
      data?.map(information => ({
        value: information.educationProfileId,
        label: `${terms[information.documentType]} (${
          information.educationalInstitution
        }, ${information.graduationYear})`,
      })),
    [data]
  );

  const [value, setValue] = useState<SelectOption>();

  useEffect(() => {
    if (!educationProfileId || !options) return;
    setValue(options.find(option => option.value === educationProfileId));
  }, [educationProfileId, options]);

  const handleChange = newValue => {
    setValue(newValue);
    const { value: educationalInformationDraftId } = newValue;

    changeEducationalInformation({
      applicationId,
      educationalInformationDraftId,
    })
      .unwrap()
      .then(() => {
        dispatch(createToast('Документ об образовании изменён', 'success'));
        dispatch(apiSlice.util.invalidateTags(['ApplicationByStaff']));
      })
      .catch(() => {
        dispatch(
          createToast(
            'Во время изменения документа об образовании произошла ошибка',
            'danger'
          )
        );
        setValue(value);
      });
  };

  return (
    <ReactSelect
      isDisabled={
        // TODO наверное можно сделать это более красиво
        (role !== UserRole.ADMIN &&
          role !== UserRole.MODERATOR &&
          role !== UserRole.SPECIALIST &&
          role !== UserRole.INSTITUTE_STAFF) ||
        !options
      }
      isLoading={isLoading}
      options={options}
      value={value}
      onChange={handleChange}
    />
  );
};

export const meanGrade = (draft: EducationInformationDraft) => {
  if (
    Number(draft.goodGrades) ||
    Number(draft.excellentGrades) ||
    Number(draft.satisfactoryGrades)
  ) {
    return String(
      (
        (4 * Number(draft.goodGrades) +
          5 * Number(draft.excellentGrades) +
          3 * Number(draft.satisfactoryGrades)) /
        (Number(draft.goodGrades) +
          Number(draft.excellentGrades) +
          Number(draft.satisfactoryGrades))
      ).toFixed(3)
    );
  } else {
    return '-';
  }
};

const ModeratorEducationPage: FC<{ isSupport?: boolean }> = ({ isSupport }) => {
  const questionnaire = useAppSelector(
    isSupport ? selectCurrentSupportQuestionnaire : selectCurrentQuestionnaire
  );
  const role = useAppSelector(selectUserRole);
  const application = useAppSelector(selectCurrentApplication);
  const dispatch = useAppDispatch();

  const document = useMemo(() => {
    if (!questionnaire || !application) return;

    return questionnaire.educationalInformationForm.find(
      education =>
        education.educationalInformationDraftId ===
        application.educationProfileId
    );
  }, [questionnaire, application]);

  const educationInfo = questionnaire?.educationalInformationForm.find(
    edu => edu.id
  );

  const DIPLOMA_TYPES = [
    EducationalDocumentType.PRIMARY_VOCATIONAL_EDUCATION_DIPLOMA,
    EducationalDocumentType.SECONDARY_VOCATIONAL_EDUCATION_DIPLOMA,
    EducationalDocumentType.BACHELOR_DEGREE,
    EducationalDocumentType.SPECIALIST_DIPLOMA,
    EducationalDocumentType.MASTER_DEGREE,
    EducationalDocumentType.POSTGRADUATE_DIPLOMA,
  ];

  const isDiploma = DIPLOMA_TYPES.includes(
    document?.documentType as EducationalDocumentType
  );

  const list = useMemo(() => {
    if (!application) return;
    if (!document)
      return [
        {
          title: termsRu.EDUCATIONAL_DOCUMENT + ':',
          label: (
            <EducationInfoSelect
              userId={application.userId}
              applicationId={application.id}
              educationalLevel={application.educationLevel}
              educationProfileId={application.educationProfileId}
            />
          ),
        },
      ];
    const result = [
      {
        title: termsRu.STUDY_LEVEL + ':',
        label: document.educationLevel ? terms[document.educationLevel] : '-',
      },
      {
        title: termsRu.DOCUMENT_TYPE + ':',
        label: document.documentType ? terms[document.documentType] : '-',
      },
      ...(isDiploma
        ? [
            {
              title: termsRu.DIRECTION_CODE + ':',
              label: document.directionCode || '-',
            },
            {
              title: termsRu.DIRECTION_NAME + ':',
              label: (
                <div className="mean-grade__modal">
                  <span>{document.directionName || '-'}</span>
                  <Button
                    onClick={() =>
                      dispatch(
                        showModal({
                          name: 'EDIT_DIRECTION_NAME_MODAL',
                          data: {
                            educationalInformationDraftId:
                              application?.educationProfileId,
                            directionCode: educationInfo?.directionCode,
                            directionName: educationInfo?.directionName,
                          },
                        })
                      )
                    }
                    theme="success"
                    size="nano"
                  >
                    <EditIcon />
                  </Button>
                </div>
              ),
            },
          ]
        : []),
      {
        title: termsRu.DOCUMENT_SERIES_AND_NUMBER + ':',
        label: document.educationalDocumentNumber || '-',
      },
      {
        title: termsRu.DATE_ISSUE + ':',
        label: document.educationalDocumentIssuedAt
          ? formatDateYYYYMMDD(document.educationalDocumentIssuedAt)
          : '-',
      },
      {
        title: 'Аттестат/Диплом с отличием:',
        label: document.withHonors ? 'Да' : 'Нет',
      },
      {
        title: termsRu.NUMBER_OF_SATISFACTORY_GRADES + ':',
        label: document.satisfactoryGrades
          ? document.satisfactoryGrades + ''
          : '-',
      },
      {
        title: termsRu.NUMBER_OF_GOOD_GRADES + ':',
        label: document.goodGrades ? document.goodGrades + '' : '-',
      },
      {
        title: termsRu.NUMBER_OF_EXCELLENT_GRADES + ':',
        label: document.excellentGrades ? document.excellentGrades + '' : '-',
      },
      {
        title: termsRu.GPA + ':',
        label: (
          <div className="mean-grade__modal">
            <span>{meanGrade(document)}</span>
            {(!application?.epguInfo?.createdFromEpgu ||
              role === UserRole.ADMIN ||
              role === UserRole.MODERATOR ||
              role === UserRole.INSTITUTE_STAFF) && (
              <Button
                onClick={() =>
                  dispatch(
                    showModal({
                      name: 'EDIT_GRADUATE_MARKS_MODAL',
                      data: {
                        educationalInformationDraftId:
                          application?.educationProfileId,
                        educationProfileId: application?.educationProfileId,
                        userId: application?.userId,
                      },
                    })
                  )
                }
                theme="success"
                size="nano"
              >
                <EditIcon />
              </Button>
            )}
          </div>
        ),
      },
      {
        title: 'Наименование ОУ:',
        label: document.educationalInstitution || '-',
      },
      {
        title: 'Страна ОУ:',
        label: document.educationCountry?.title || '-',
      },
      {
        title: 'Год окончания ОУ:',
        label: document.graduationYear + '' || '-',
      },
      {
        title: 'Учился на подготовительном отделении в СПбПУ:',
        label: document.studiedSpbpuPreparatoryDepartment ? 'Да' : 'Нет',
      },
      {
        title: 'Статус проверки в ССПВО:',
        label: document.checkStatus
          ? checkStatusesToStrings[document.checkStatus]
          : '-',
      },
      {
        title: 'Документ об образовании:',
        label: (
          <EducationInfoSelect
            userId={application.userId}
            applicationId={application.id}
            educationalLevel={application.educationLevel}
            educationProfileId={application.educationProfileId}
          />
        ),
      },
    ];
    if (role === UserRole.MODERATOR) return result;
    return result.toSpliced(
      -1,
      0,
      {
        title: 'ЕПГУ GUID документа:',
        label: document.epguExternalId ? document.epguExternalId : '-',
      },
      {
        title: 'ЕПГУ GUID документа с отличием:',
        label: document.epguWithHonorsDocumentGuid
          ? document.epguWithHonorsDocumentGuid
          : '-',
      }
    );
  }, [application, document]);

  if (!questionnaire || !application) {
    return <Loader />;
  }

  return (
    <div className="moderator-education-page">
      {!document && (
        <b>
          Не удалось найти документ об образовании, указанный во время
          заполнения заявления. Выберите другой из списка ниже:
        </b>
      )}
      {list && <ModeratorDataList list={list} />}
      <CheckListEntryValueSelectWithRoleChecker
        getValue={checkList =>
          checkList.unrelatedCheckMarks['educational-background']
        }
        updateValue={newValue => ({
          unrelatedCheckMarks: {
            'educational-background': newValue,
          },
        })}
      />
    </div>
  );
};

export default ModeratorEducationPage;
